import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  listOrganizations,
  PaginatedOrganizationsResponse,
} from "@/services/organizationsApi";
import { grantListBadgeTitle } from "@/lib/billingGrant";
import { tierBadgeColor, tierBadgeLabel } from "@/lib/tierBadge";

type BusinessFilter = "all" | "business" | "personal";

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedOrganizationsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [businessFilter, setBusinessFilter] = useState<BusinessFilter>("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const isBusiness =
          businessFilter === "business"
            ? true
            : businessFilter === "personal"
            ? false
            : undefined;
        const res = await listOrganizations(navigate, {
          page,
          size,
          search: search || undefined,
          isBusiness,
        });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, search, businessFilter]);

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  // Tier B2B de l'organisation (starter|business|enterprise). Le subscription_type
  // B2C legacy est conservé en tooltip (title) car peu pertinent ici.
  const tierBadge = (tier: string | null | undefined, b2cType: string | null | undefined) => (
    <span
      title={`Abonnement B2C : ${b2cType ?? "—"}`}
      className={`px-2 py-0.5 rounded text-xs font-medium ${tierBadgeColor(tier)}`}
    >
      {tierBadgeLabel(tier)}
    </span>
  );

  const businessBadge = (isBusiness: boolean) =>
    isBusiness ? (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
        Entreprise
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
        Perso
      </span>
    );

  const onSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Organisations</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-4 justify-between">
            <h2 className="text-xl font-semibold">
              Liste des organisations B2B
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <form
                onSubmit={onSubmitSearch}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  placeholder="Recherche nom / slug / business"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="border rounded px-2 py-1 text-sm w-72"
                />
                <Button type="submit" variant="outline" size="sm">
                  Rechercher
                </Button>
              </form>
              <label className="text-sm text-gray-600">Type</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={businessFilter}
                onChange={(e) => {
                  setPage(1);
                  setBusinessFilter(e.target.value as BusinessFilter);
                }}
              >
                <option value="all">Toutes</option>
                <option value="business">Entreprises</option>
                <option value="personal">Perso (B2C)</option>
              </select>
              <label className="text-sm text-gray-600">Taille page</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={size}
                onChange={(e) => {
                  setPage(1);
                  setSize(parseInt(e.target.value, 10));
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && (
            <div className="text-gray-500 text-sm">Chargement...</div>
          )}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Nom</th>
                  <th className="px-3 py-2 text-left">Slug</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Tier</th>
                  <th className="px-3 py-2 text-right">Sièges</th>
                  <th className="px-3 py-2 text-right">Membres</th>
                  <th className="px-3 py-2 text-right">Équipes</th>
                  <th className="px-3 py-2 text-right">Campagnes</th>
                  <th className="px-3 py-2 text-left">Stripe</th>
                  <th className="px-3 py-2 text-left">Créée</th>
                  <th className="px-3 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((o) => (
                  <tr key={o.id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{o.id}</td>
                    <td className="px-3 py-2 font-medium">{o.name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{o.slug}</td>
                    <td className="px-3 py-2">{businessBadge(o.is_business)}</td>
                    <td className="px-3 py-2">
                      {tierBadge(o.tier, o.subscription_type)}
                      {o.billing_grant_active && (
                        <span
                          title={grantListBadgeTitle(o.billing_grant_tier, o.billing_grant_ends_at)}
                          className="ml-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800"
                        >
                          Offert
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {o.active_seats}/{o.purchased_seats}
                    </td>
                    <td className="px-3 py-2 text-right">{o.member_count}</td>
                    <td className="px-3 py-2 text-right">{o.team_count}</td>
                    <td className="px-3 py-2 text-right">{o.campaign_count}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {o.stripe_customer_id || "-"}
                    </td>
                    <td className="px-3 py-2">
                      {o.created_at
                        ? new Date(o.created_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/organizations/${o.id}`}
                        className="text-blue-600 hover:underline"
                      >
                        Voir
                      </Link>
                    </td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr>
                    <td
                      className="px-3 py-4 text-center text-gray-500"
                      colSpan={12}
                    >
                      Aucune organisation
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {data?.page ?? page} / {data?.pages ?? 1} —{" "}
              {data?.total ?? 0} éléments
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
